import { PageController } from '@client/components/_core/layout/PageController';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { message } from '@client/components/_ui/feedback/message';
import { ReleaseEditor } from '@client/components/whats-new-admin/ReleaseEditor';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { createFileRoute } from '@tanstack/react-router';
import { Skeleton } from 'antd';
import { parseReleaseRouteId } from './parse-release-route-id';

export const Route = createFileRoute('/admin-console/platform-content/whats-new/$id')({
    staticData: {
        breadcrumb: { title: 'Release' },
        ui: { title: 'Edit release', subtitle: 'Changes to a published release go live as soon as they are saved' },
    },
    params: {
        parse: ({ id }) => ({ id: parseReleaseRouteId(id) }),
    },
    component: EditReleaseRoute,
});

function EditReleaseRoute() {
    return (
        <PageController route={Route}>
            <EditRelease />
        </PageController>
    );
}

function EditRelease() {
    const { id } = Route.useParams();
    const releaseId = BigInt(id);
    const { trpc, trpcUtils } = useTrpc();
    const { isReadOnly } = useAdminModulePermissions('content');

    const { data, isPending, error, refetch } = trpc.admin.whatsNew.getRelease.useQuery(
        { id: releaseId },
        { refetchOnWindowFocus: false },
    );

    const invalidate = async () => {
        void trpcUtils.admin.whatsNew.listReleases.invalidate();
        void trpcUtils.whatsNew.listReleases.invalidate();
        // Keep the editor locked until its keyed server snapshot has been replaced.
        await trpcUtils.admin.whatsNew.getRelease.invalidate({ id: releaseId });
    };

    const update = trpc.admin.whatsNew.updateRelease.useMutation({
        onError: (mutationError) => errorMessage.show(mutationError),
    });
    const publish = trpc.admin.whatsNew.publishRelease.useMutation({
        onError: (mutationError) => errorMessage.show(mutationError),
        onSuccess: () => {
            message.success('Published');
        },
    });
    const archive = trpc.admin.whatsNew.archiveRelease.useMutation({
        onError: (mutationError) => errorMessage.show(mutationError),
        onSuccess: async () => {
            await invalidate();
            message.success('Archived');
        },
    });

    if (error) {
        return <InlineQueryError error={error} onRetry={() => void refetch()} title="Couldn't load the release" />;
    }
    if (isPending || !data) {
        return <Skeleton active paragraph={{ rows: 8 }} />;
    }

    return (
        <ReleaseEditor
            // Remount when the row changes (publish/archive) so the form's initial values reset cleanly.
            key={`${data.id}-${data.status}-${data.updatedAt.getTime()}`}
            mode="edit"
            initial={data}
            isReadOnly={isReadOnly}
            isSaving={update.isPending}
            isPublishing={publish.isPending}
            isArchiving={archive.isPending}
            onSave={async (input) => {
                await update.mutateAsync({ id: releaseId, set: input, expectedUpdatedAt: data.updatedAt });
                await invalidate();
                message.success('Saved');
            }}
            onPublish={async (input) => {
                await update.mutateAsync({ id: releaseId, set: input, expectedUpdatedAt: data.updatedAt });
                try {
                    await publish.mutateAsync({ id: releaseId });
                } finally {
                    // Also refresh a successfully saved draft if publication fails.
                    await invalidate();
                }
            }}
            onArchive={async () => {
                await archive.mutateAsync({ id: releaseId });
            }}
        />
    );
}
